import { useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { Loader2, TrendingUp, Camera, Info, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '../../lib/utils';
import { PALETTE, SCOPES, METRICS, RANGES, fmtPeriod } from './capabilityAnalyticsUtils';

function TrendChart({ trend, scope, metric, periods }) {
  const { data, series } = useMemo(() => {
    const pers = periods || trend.periods || [];
    if (scope === 'org') {
      const byP = Object.fromEntries((trend.org || []).map(p => [p.period, p]));
      const rows = pers.map(p => ({
        period: fmtPeriod(p),
        'Role coverage': byP[p]?.role_coverage_pct ?? null,
        'Avg coverage': byP[p]?.avg_coverage_pct ?? null,
      }));
      return { data: rows, series: ['Role coverage', 'Avg coverage'] };
    }
    const entities = scope === 'cohort' ? (trend.cohorts || []) : (trend.roles || []);
    const rows = pers.map(p => {
      const row = { period: fmtPeriod(p) };
      entities.forEach(e => {
        const pt = e.series.find(s => s.period === p);
        row[e.name] = pt ? (pt[metric] ?? pt.avg_coverage_pct ?? null) : null;
      });
      return row;
    });
    return { data: rows, series: entities.map(e => e.name) };
  }, [trend, scope, metric, periods]);

  if (!data.length) {
    return <div className="py-16 text-center text-sm text-slate-400" data-testid="cap-trend-empty">No data points in this range.</div>;
  }
  return (
    <div className="h-[340px] w-full" data-testid="cap-trend-chart">
      <ResponsiveContainer width="100%" height="100%" minHeight={300}>
        <LineChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: -12 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
          <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
          <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} unit="%" width={44} />
          <Tooltip
            contentStyle={{ fontSize: 12, borderRadius: 10, border: '1px solid #e2e8f0', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}
            formatter={(v) => (v == null ? '—' : `${v}%`)}
          />
          {series.length <= 8 && <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} iconType="plainline" />}
          {series.map((s, i) => (
            <Line key={s} type="monotone" dataKey={s} stroke={PALETTE[i % PALETTE.length]}
              strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} connectNulls />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function TrendCard({
  trend, scope, setScope, metric, setMetric, range, setRange, custom, setCustom,
  visiblePeriods, canAdmin, capturing, onCapture, yrLoading, onYearReview,
}) {
  const latest = trend?.latest_captured_at ? new Date(trend.latest_captured_at).toLocaleDateString() : null;
  return (
    <Card className="border border-slate-200 shadow-sm">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-cyan-600" /> Coverage trend
            <span className="text-[11px] font-normal text-slate-400">{visiblePeriods.length} month{visiblePeriods.length !== 1 ? 's' : ''} shown</span>
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={range} onValueChange={setRange}>
              <SelectTrigger className="h-8 w-[180px] bg-white border-slate-200 text-xs" data-testid="cap-trend-range">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RANGES.map(r => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5" data-testid="cap-trend-scope">
              {SCOPES.map(s => (
                <button key={s.id} onClick={() => setScope(s.id)}
                  className={cn('text-xs px-2.5 py-1 rounded-md font-medium transition-colors',
                    scope === s.id ? 'bg-white text-cyan-700 shadow-sm' : 'text-slate-500 hover:text-slate-700')}
                  data-testid={`cap-trend-scope-${s.id}`}>
                  {s.label}
                </button>
              ))}
            </div>
            {scope !== 'org' && (
              <Select value={metric} onValueChange={setMetric}>
                <SelectTrigger className="h-8 w-[150px] bg-white border-slate-200 text-xs" data-testid="cap-trend-metric">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {METRICS.map(m => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {canAdmin && (
              <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5"
                onClick={onCapture} disabled={capturing} data-testid="cap-snapshot-btn">
                {capturing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
                Capture snapshot
              </Button>
            )}
            <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5 border-cyan-300 text-cyan-700 hover:bg-cyan-50"
              onClick={onYearReview} disabled={yrLoading} data-testid="cap-year-review-btn">
              {yrLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
              Annual report
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {range === 'custom' && (
          <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="cap-trend-custom-range">
            <span className="text-xs text-slate-500">From</span>
            <input type="month" value={custom.from} onChange={(e) => setCustom(c => ({ ...c, from: e.target.value }))}
              className="h-8 text-xs border border-slate-200 rounded-md px-2 focus:outline-none focus:ring-1 focus:ring-cyan-400"
              data-testid="cap-trend-custom-from" />
            <span className="text-xs text-slate-500">to</span>
            <input type="month" value={custom.to} onChange={(e) => setCustom(c => ({ ...c, to: e.target.value }))}
              className="h-8 text-xs border border-slate-200 rounded-md px-2 focus:outline-none focus:ring-1 focus:ring-cyan-400"
              data-testid="cap-trend-custom-to" />
          </div>
        )}
        {trend && <TrendChart trend={trend} scope={scope} metric={metric} periods={visiblePeriods} />}
        {latest && (
          <p className="text-[11px] text-slate-400 mt-2 flex items-center gap-1">
            <Info className="h-3 w-3" /> Newest point {latest}
            {trend?.latest_source === 'synthetic' ? ' · demo history is synthetic' : ''}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
